// CHG-1287 / ITER-3412 — v2 adoption telemetry event types + batch client.
//
// Privacy-by-design: every event payload is TypeScript-typed against the
// allowlist below. Adding a field requires extending the allowlist + the
// backend Pydantic schema. The backend rejects unrecognized fields, so
// even an accidental client-side leak fails closed.
//
// NO PII. No user names, no entity IDs (donor/donation/etc.), no email,
// no phone, no addresses, no form values, no page content. Only:
// event_name + org-level identifiers + brand + hostname + route shape +
// timestamp. The full enumeration lives at
// specs/seeds/dashboard-v2/adoption-telemetry-events.yaml.
//
// Batching: events queue in memory, flush every FLUSH_INTERVAL_MS, on
// beforeunload (via sendBeacon), or when the queue exceeds MAX_QUEUE_SIZE.

export type V2EventName =
  | "dashboard_v2.session_started"
  | "dashboard_v2.page_view"
  | "dashboard_v2.welcome_completed"
  | "dashboard_v2.welcome_dismissed"
  | "dashboard_v2.classic_fallback_clicked";

// Allowlist of payload fields. The Pydantic schema on the backend mirrors
// this; adding a new field is a 2-file change (FE allowlist + BE schema)
// + a privacy-doc review.
export interface AdoptionEventPayload {
  /** Org UUID; coarse-grained — no individual user identity. */
  org_id?: string;
  /** Brand variant ID (e.g. "_default", "mohseen", "alorachurch"). */
  brand_id?: string;
  /** Hostname signature — sha-256 of window.location.hostname trimmed to
   *  first 12 hex chars. Lets product distinguish "users on staging vs prod"
   *  without storing the hostname itself. */
  hostname_signature?: string;
  /** Document referrer hostname signature (same hashing as hostname_signature). */
  referrer_signature?: string;
  /** Route shape — pathname with all `:id` segments replaced by `:id` and
   *  numeric segments replaced by `:n`. E.g. /donors/abc123 → /donors/:id. */
  route_shape?: string;
  /** Entity surface — coarse category (e.g. "donor", "campaign"). NEVER
   *  an entity ID. Comes from a static map of route_shape → entity type. */
  surface_id?: string;
  /** Surface this event originated from, e.g. "welcome-overlay-step-4",
   *  "profile-menu-classic-link". */
  source?: string;
}

export interface AdoptionEvent {
  event_name: V2EventName;
  occurred_at: string;   // ISO-8601 UTC
  payload: AdoptionEventPayload;
}

// ─────────────────────────────────────────────────────────────────
// SHA-256 hostname signature helper. Falls back to a constant when
// crypto.subtle isn't available (older browsers / test envs); the
// "fallback" string makes the missing-crypto case visible.
// ─────────────────────────────────────────────────────────────────

export async function hostnameSignature(hostname: string): Promise<string> {
  if (!hostname) return "";
  if (typeof crypto === "undefined" || !crypto.subtle) {
    return "fallback-no-crypto";
  }
  try {
    const buf = new TextEncoder().encode(hostname.toLowerCase());
    const hash = await crypto.subtle.digest("SHA-256", buf);
    const bytes = new Uint8Array(hash);
    let hex = "";
    for (let i = 0; i < bytes.length && hex.length < 12; i++) {
      hex += bytes[i].toString(16).padStart(2, "0");
    }
    return hex.slice(0, 12);
  } catch {
    return "fallback-error";
  }
}

// Build the route_shape from a pathname — replaces UUID-like and numeric
// segments. Pure / deterministic / safe to call at every navigation.
export function routeShape(pathname: string): string {
  if (!pathname) return "/";
  return pathname
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      // UUID v4 shape
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) {
        return ":id";
      }
      // Generic non-version-string hex (8+ chars) — most ids
      if (/^[0-9a-f]{8,}$/i.test(seg) && !/^\d{8,}$/.test(seg)) return ":id";
      // Purely numeric segment
      if (/^\d+$/.test(seg)) return ":n";
      return seg;
    })
    .join("/");
}

// ─────────────────────────────────────────────────────────────────
// Telemetry batch client — used by AdoptionTelemetry.tsx
// ─────────────────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 30_000;
const MAX_QUEUE_SIZE = 50;
const ENDPOINT = "/telemetry/adoption";

type Transport = (events: AdoptionEvent[]) => Promise<void>;

export class TelemetryBatch {
  private queue: AdoptionEvent[] = [];
  private timerId: number | null = null;
  private transport: Transport;

  constructor(transport: Transport) {
    this.transport = transport;
  }

  enqueue(event: AdoptionEvent): void {
    this.queue.push(event);
    if (this.queue.length >= MAX_QUEUE_SIZE) {
      void this.flush();
    } else if (this.timerId === null && typeof window !== "undefined") {
      this.timerId = window.setTimeout(() => {
        this.timerId = null;
        void this.flush();
      }, FLUSH_INTERVAL_MS);
    }
  }

  async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0);
    if (this.timerId !== null && typeof window !== "undefined") {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
    try {
      await this.transport(batch);
    } catch {
      // Drop on failure — telemetry must never block app behavior.
      // Future enhancement: persist to localStorage for retry.
    }
  }

  /** For testing — inspect the queue without flushing. */
  peekQueue(): readonly AdoptionEvent[] {
    return [...this.queue];
  }
}

export { ENDPOINT, FLUSH_INTERVAL_MS, MAX_QUEUE_SIZE };
